import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * The one dialog shell every stock modal is built from.
 *
 * Escape closes, the overlay closes, focus lands inside on open, and the page
 * behind cannot scroll away underneath. Written once here because a warehouse
 * user on a tablet meets these dialogs a dozen times a day, and four dialogs
 * that each behave slightly differently is how a system stops feeling built.
 */
export default function StockModal({ title, subtitle, children, footer, onClose, width }) {
    const bodyRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    useEffect(() => {
        const first = bodyRef.current?.querySelector(
            'input:not([type="hidden"]), select, textarea, button'
        );
        first?.focus();
    }, []);

    return (
        <div className="stk__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="stk__modal" style={width ? { maxWidth: width } : undefined} role="dialog" aria-modal="true" aria-label={title}>
                <header className="stk__modalHead">
                    <div>
                        <h3 className="stk__modalTitle">{title}</h3>
                        {subtitle ? <p className="stk__modalSub">{subtitle}</p> : null}
                    </div>
                    <button type="button" className="stk__modalClose" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </header>

                <div className="stk__modalBody" ref={bodyRef}>{children}</div>

                {footer ? <footer className="stk__modalFoot">{footer}</footer> : null}
            </div>
        </div>
    );
}

/**
 * Are you sure? — for the actions that have no undo worth relying on.
 *
 * ONE PRESS IS NOT A DECISION. Archive sits next to Edit on every catalogue
 * card, and a mis-tap took an item off the list for everybody with no
 * indication anything had happened. It is not destructive — the history
 * survives and it can be restored — but it is not something to do by accident
 * either, so it costs a second press and says what it will do.
 */
export function ConfirmModal({
    title, message, confirmLabel, tone = 'primary', onClose, onConfirm,
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const go = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onConfirm();
        } catch (err) {
            setError(err.response?.data?.error || 'That did not work. Try again.');
            setBusy(false);
        }
    };

    return (
        <StockModal title={title} onClose={onClose} width={460}>
            <p className="stk__modalIntro">{message}</p>
            {error ? <p className="stk__formError">{error}</p> : null}
            <div className="stk__modalActions">
                <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>
                    Keep it
                </button>
                <button type="button" className={`stk__btn stk__btn--${tone}`} onClick={go} disabled={busy}>
                    {busy ? 'Working…' : confirmLabel}
                </button>
            </div>
        </StockModal>
    );
}

/**
 * Confirm-with-a-note. Deliver and cancel are the same shape — a sentence of
 * context, one text box, one button — so they are the same component.
 * `required` makes the note mandatory, which cancelling is (the requester is
 * told the reason) and delivering is not.
 */
export function NoteModal({
    title, intro, label, placeholder, confirmLabel,
    tone = 'primary', required = false, onClose, onSubmit,
}) {
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (required && !note.trim()) {
            setError('This one needs a reason.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await onSubmit(note.trim());
        } catch (err) {
            setError(err.response?.data?.error || 'That did not save. Try again.');
            setSaving(false);
        }
    };

    return (
        <StockModal title={title} onClose={onClose} width={520}>
            <form onSubmit={submit}>
                {intro ? <p className="stk__modalIntro">{intro}</p> : null}

                <label className="stk__field">
                    <span className="stk__fieldLabel">{label}</span>
                    <textarea
                        className="stk__textarea"
                        rows={3}
                        value={note}
                        placeholder={placeholder}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </label>

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>
                        Close
                    </button>
                    <button type="submit" className={`stk__btn stk__btn--${tone}`} disabled={saving}>
                        {saving ? 'Saving…' : confirmLabel}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
